import React from "react";

import { object, string, number, date, InferType } from 'yup';
import snakeToPascal from "../../../tools";
import ExcelValue from "../Input/Types";
import dayjs from "dayjs";
import apiService from "../../../services/ApiService";
import TailwindInput from "../../Tailwind/TailwindInput";
import TailwindSelect from "../../Tailwind/TailwindSelect";
import TailwindDatePicker from "../../Tailwind/TailwindDatePicker";
import { cn } from "../../../lib/utils";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";

export default function SensitivityListField({ item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const sensitivityValue = (sensitivityField) ? sensitivityField.value.value : field.currentValue.value

    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    const ensureSensitivityField = async () => {
        const existing = item.sensitivityModelData.fields[field.name];
        if (existing) return existing;

        const response = await apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null);
        if (!response) return null;
        const newSF = await response.json();
        item.sensitivityModelData.fields[field.name] = newSF;
        return newSF;
    }

    const addNewElem = async () => {
        const newElem: any = {};
        const apiElem: any = {};

        Object.keys(field.elems).map((e: any) => {
            newElem[e] = { value : field.elems[e].default, address: null };
            apiElem[e] = null;
        });

        triggerSave();
        const sf = await ensureSensitivityField();
        if (!sf) { resolveSave(); return; }

        apiService.postSensitivityListElem(sf.id, apiElem).then(async(response) => {
            if (response) {
                const resp = await response.json()

                newElem['id'] = resp.id;

                sf.value.value.push(newElem);
                resolveSave();
                invalidateQueries();
            }
        })
    }

    const floatSchema = object({
        value: number()
    })

    const updateListElemWithValidation = (elem, key, type, value, index) => {
        if (type == 'float') {
            if (floatSchema.isValidSync({value: value}) || !value.length) {
                elem[key].error = null;
                updateListElem(elem, key, value, index);
            }
            else {
                elem[key].error = "Should be a Float."
                invalidateQueries();
            }
        } else {
            updateListElem(elem, key, value, index);
        }
    }

    const updateListElem = async (elem, key, value, index) => {
        const apiElem = {};

        triggerSave();
        const sf = await ensureSensitivityField();
        if (!sf) { resolveSave(); return; }

        const actualElem = sf.value.value[index];

        Object.keys(actualElem).map((e: any) => {
            if (field.elems[e] && field.elems[e].type == 'float') {
                apiElem[e] = actualElem[e].value ? actualElem[e].value.toString().replace(',', '.') : null;
            } else {
                apiElem[e] = (actualElem[e].value != 'null') ? actualElem[e].value : null;
            }
        });

        apiElem[key] = (value !== undefined) ? value : null;

        apiService.putSensitivityListElem(actualElem.id, apiElem).then(async(response) => {
            if (response) {
                actualElem[key].value = value;
                resolveSave();
                invalidateQueries();
            }
        })
    }

    const updateListDateElem = async (elem, key, value, index) => {
        const apiElem = {};

        triggerSave();
        const sf = await ensureSensitivityField();
        if (!sf) { resolveSave(); return; }

        const actualElem = sf.value.value[index];

        Object.keys(actualElem).map((e: any) => {
            if (field.elems[e] && field.elems[e].type == 'float') {
                apiElem[e] = actualElem[e].value ? actualElem[e].value.toString().replace(',', '.') : null;
            } else {
                apiElem[e] = (actualElem[e].value != 'null') ? actualElem[e].value : null;
            }
        });

        apiElem[key] = (value !== undefined) ? value : null;

        apiService.putSensitivityListElem(actualElem.id, apiElem).then(async(response) => {
            if (response) {
                actualElem[key].value = dayjs(value);
                resolveSave();
                invalidateQueries();
            }
        })
    }

    const deleteListElem = async (elem, index) => {
        triggerSave();
        const sf = await ensureSensitivityField();
        if (!sf) { resolveSave(); return; }

        const actualElem = sf.value.value[index];

        apiService.deleteSensitivityListElem(actualElem.id).then(async(response) => {
            if (response) {
                sf.value.value.splice(index, 1);
                resolveSave();
                invalidateQueries();
            }
        })
    }

    // Fetch relation options per elem key from API
    const [relationOptionsMap, setRelationOptionsMap] = React.useState<Record<string, any[]>>({});
    const [timingOptionsItems, setTimingOptionsItems] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        setOptionsLoading(true);
        const promises: Promise<void>[] = [];

        Object.keys(field.elems).forEach((key) => {
            if (field.elems[key].type === 'relation') {
                const p = apiService.getListRelationOptions(field.id, key).then(async (response) => {
                    if (response) {
                        const data = await response.json();
                        setRelationOptionsMap(prev => ({ ...prev, [key]: data }));
                    }
                });
                promises.push(p);
            }
        });

        const hasTimingRef = Object.values(field.elems).some((elem: any) => elem.type === 'timing_reference');
        if (hasTimingRef) {
            const p = apiService.getTimingOptions(field.id).then(async (response) => {
                if (response) {
                    const data = await response.json();
                    setTimingOptionsItems(data);
                }
            });
            promises.push(p);
        }

        Promise.all(promises).then(() => setOptionsLoading(false));
    }, [field.id]);

    const getFilteredRelationOptions = (key, index) => {
        const allOptions = relationOptionsMap[key] || [];
        const elem = field.elems[key];
        const alreadySelected = (elem.unique && sensitivityValue)
            ? sensitivityValue.map((e, i) => (i !== index && e[key].value != null) ? e[key].value.toString() : null)
            : [];
        return allOptions.filter(r => !alreadySelected.includes(r.id.toString()));
    }

    const getListElemSelectorChoices = (elem, key, index) => {
        const targetField = sensitivityField
            ? item.sensitivityModelData?.fields?.[elem.target_list_field]
            : item.fields?.[elem.target_list_field];

        if (!targetField) {
            return [];
        }

        const alreadySelected = (elem.unique) ? sensitivityValue.map((e, i) => (i !== index) ? e[key].value : null) : [];

        return targetField.value.value.filter(e => !alreadySelected.includes(e.id))

    }

    const setListElemSelectorChoices = (listElemSelectorChoices, elem, key) => {
        listElemSelectorChoices.map((e: any) => {
            if (e.id == elem[key].value) {
                elem[key].currentObj = e
            }
        })
        if (listElemSelectorChoices.length == 0 || elem[key].currentObj === undefined) {
            elem[key].currentObj = 'null';
        }
    }

    const setTimingById = (elem, key) => {
        timingOptionsItems.forEach((e: any) => {
            if (e.id == elem[key].value) {
                elem[key].currentObj = e
            }
        })
        if (elem[key].currentObj === undefined) {
            elem[key].currentObj = 'null';
        }
    }

    const setCurrentRelationObj = (potentialRelations, elem, key) => {
        potentialRelations.forEach(e => {
            if (e.id == elem[key].value) {
                elem[key].currentObj = e
            }
        })
        if (elem[key].currentObj === undefined) {
            elem[key].currentObj = 'null';
        }
    }

    const choicePanelName = (relationTypes) => {
        let name = "";
        let index = 0;
        relationTypes.forEach(e => {
            if (index != 0) {
                name += ' | '
            }
            name += e;
            index++;
        });
        return name;
    }

    const [datePickerStates, setDatePickerStates] = React.useState<{[key: string]: boolean}>({});

    const setDatePickerOpen = (elemId, key, value) => {
        const stateKey = `${elemId}-${key}`;
        setDatePickerStates(prev => ({
            ...prev,
            [stateKey]: value
        }));
    };

    const getDatePickerOpen = (elemId, key) => {
        const stateKey = `${elemId}-${key}`;
        return datePickerStates[stateKey] || false;
    };

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    // setSliderValue(100);
                    // setFieldValue(originalValue)
                    invalidateQueries();
                }
            }
        )
        
    }


    return (
        <div className="mt-1 mr-2">
            <div className="flex flex-row">
                {!isReadOnly && (
                    <div className="flex flex-col mr-2">
                        <button
                            onClick={resetSensitivityValue}
                            disabled={!sensitivityField}
                            className="h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                        </button>
                    </div>
                )}
                <div className="overflow-x-auto flex-1 min-w-0">
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden w-fit">
                    <table className="w-full">
                        <tbody>
                            <tr className="border-b border-slate-200">
                                {Object.keys(field.elems).map(key => (
                                    <td key={key} className="whitespace-nowrap px-2 py-2">
                                        <b>{snakeToPascal(key)}</b>
                                    </td>
                                ))}
                            </tr>
                            {
                                sensitivityValue.map((e: any, index) => (
                                    <tr key={e.id || index} className="border-b border-slate-100">
                                        {
                                            Object.keys(field.elems).map((key) => {
                                                if (key !== 'id') {
                                                    if (field.elems[key].type === 'relation') {
                                                        const potentialRelations = getFilteredRelationOptions(key, index);
                                                        setCurrentRelationObj(potentialRelations, e, key);

                                                        const relationOptions = [
                                                            { value: '', label: '— Select —' },
                                                            ...potentialRelations.map((rel: any) => ({
                                                                value: rel.id.toString(),
                                                                label: rel.name,
                                                            }))
                                                        ];

                                                        const currentRelationValue = e[key].currentObj !== 'null' && e[key].currentObj
                                                            ? e[key].currentObj.id.toString()
                                                            : '';

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={currentRelationValue}
                                                                    onChange={(newValue) => {
                                                                        if (!newValue) {
                                                                            e[key].currentObj = 'null';
                                                                            updateListElem(e, key, null, index);
                                                                        } else {
                                                                            const selectedRelation = potentialRelations.find(r => r.id.toString() === newValue);
                                                                            if (selectedRelation) {
                                                                                e[key].currentObj = selectedRelation;
                                                                                updateListElem(e, key, selectedRelation.id, index);
                                                                            }
                                                                        }
                                                                    }}
                                                                    options={relationOptions}
                                                                    placeholder={snakeToPascal(choicePanelName(field.elems[key].relation_types))}
                                                                    disabled={isReadOnly}
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'timing_reference') {
                                                        setTimingById(e, key);

                                                        const selectOptions = [
                                                            {
                                                                value: 'null',
                                                                label: 'Timing',
                                                                isItalic: true
                                                            },
                                                            ...timingOptionsItems.map((timing: any) => ({
                                                                value: timing,
                                                                label: snakeToPascal(timing.field_name)
                                                            }))
                                                        ];

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={e[key].currentObj}
                                                                    onChange={(value) => {
                                                                        e[key].currentObj = value;
                                                                        updateListElem(e, key, value.id, index);
                                                                    }}
                                                                    options={selectOptions}
                                                                    disabled={isReadOnly}
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'list_elem_selector') {

                                                        const choicesItems = getListElemSelectorChoices(field.elems[key], key, index);
                                                        setListElemSelectorChoices(choicesItems, e, key)

                                                        const selectOptions = [
                                                            {
                                                                value: 'null',
                                                                label: snakeToPascal(field.elems[key].target_list_field),
                                                                isItalic: true
                                                            },
                                                            ...choicesItems.map((choice: any) => ({
                                                                value: choice,
                                                                label: choice[field.elems[key].target_list_column_anchor].value
                                                                    ? snakeToPascal(choice[field.elems[key].target_list_column_anchor].value)
                                                                    : " "
                                                            }))
                                                        ];

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={e[key].currentObj}
                                                                    onChange={(value) => {
                                                                        e[key].currentObj = value;
                                                                        updateListElem(e, key, value.id, index);
                                                                    }}
                                                                    options={selectOptions}
                                                                    disabled={isReadOnly}
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'datetime') {
                                                        const dateValue = e[key].value ? (dayjs.isDayjs(e[key].value) ? e[key].value.toDate() : new Date(e[key].value)) : null;

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <div className="w-[200px]">
                                                                    <TailwindDatePicker
                                                                        value={dateValue}
                                                                        onChange={(value) => {
                                                                            if (value) {
                                                                                const formattedDate = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
                                                                                updateListDateElem(e, key, formattedDate, index);
                                                                            }
                                                                        }}
                                                                        disabled={isReadOnly}
                                                                    />
                                                                </div>
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'enum') {
                                                        e[key].currentObj = (e[key].value) ? e[key].value : 'null';

                                                        const selectOptions = [
                                                            {
                                                                value: 'null',
                                                                label: snakeToPascal(key),
                                                                isItalic: true
                                                            },
                                                            ...field.elems[key].choices.map(choice => ({
                                                                value: choice,
                                                                label: snakeToPascal(choice)
                                                            }))
                                                        ];

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={e[key].currentObj}
                                                                    onChange={(value) => {
                                                                        e[key].currentObj = value;
                                                                        updateListElem(e, key, value, index);
                                                                    }}
                                                                    options={selectOptions}
                                                                    disabled={isReadOnly}
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else {
                                                        return (
                                                            <td key={key} className="p-0">
                                                                <input
                                                                    type="text"
                                                                    disabled={isReadOnly}
                                                                    key={e.id}
                                                                    defaultValue={e[key].value}
                                                                    onChange={(ev) => {
                                                                        updateListElemWithValidation(e, key, field.elems[key].type, ev.target.value, index);
                                                                    }}
                                                                    placeholder={(field.elems[key].type === 'float') ? "0": snakeToPascal(key)}
                                                                    className={cn(
                                                                        "w-[200px] h-[42px] px-4 border-0 focus:outline-none text-sm",
                                                                        e[key].error ? "text-red-600" : "text-slate-900",
                                                                        isReadOnly && "opacity-50 cursor-not-allowed"
                                                                    )}
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                }
                                            })
                                        }
                                        {!isReadOnly && (
                                            <td className="p-0">
                                                <button
                                                    onClick={() => {
                                                        deleteListElem(e, index);
                                                    }}
                                                    className="mx-1 mt-2 text-slate-600 hover:text-red-600 transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
                </div>
            </div>
            <div className="mt-1">
                {!isReadOnly && (
                    <button
                        onClick={() => {
                            addNewElem();
                        }}
                        className="ml-1 text-slate-600 hover:text-primary transition-colors"
                    >
                        <span className="material-symbols-outlined text-[20px]">add</span>
                    </button>
                )}
                <SaveIndicator status={saveStatus} className="ml-2 inline-flex" />
            </div>
        </div>
    )
}
